import React, { useState } from "react";
import { useStoreAuth } from "../../contexts/StoreAuthContext";

interface AuthDialogProps {
    open: boolean;
    onClose: () => void;
}

type AuthMode = "login" | "register";

const AuthDialog: React.FC<AuthDialogProps> = ({ open, onClose }: AuthDialogProps) => {
    const { login, register, isLoading } = useStoreAuth();
    const [mode, setMode] = useState<AuthMode>("login");
    const [formState, setFormState] = useState({ name: "", email: "", password: "" });
    const [error, setError] = useState<string | null>(null);

    if (!open) return null;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);
        try {
            if (mode === "login") {
                await login({ email: formState.email, name: formState.name || undefined });
            } else {
                if (!formState.password || formState.password.length < 6) {
                    setError("Password must be at least 6 characters");
                    return;
                }
                await register({ email: formState.email, password: formState.password, name: formState.name });
            }
            onClose();
        } catch (err) {
            setError((err as Error).message);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl bg-midnight-800 border border-white/10 p-8 shadow-drawer">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold tracking-wide">{mode === "login" ? "Sign in" : "Create account"}</h2>
                    <button
                        onClick={onClose}
                        className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 transition"
                        aria-label="Close auth dialog"
                    >
                        ×
                    </button>
                </div>

                <div className="flex gap-2 mb-6 text-sm">
                    <button
                        onClick={() => setMode("login")}
                        className={`flex-1 rounded-full px-4 py-2 ${mode === "login" ? "bg-white text-black" : "bg-white/5 text-white/70"
                            }`}
                    >
                        Sign in
                    </button>
                    <button
                        onClick={() => setMode("register")}
                        className={`flex-1 rounded-full px-4 py-2 ${mode === "register" ? "bg-white text-black" : "bg-white/5 text-white/70"
                            }`}
                    >
                        Create account
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <label className="block text-sm text-white/80">
                        Full name (optional)
                        <input
                            type="text"
                            name="name"
                            value={formState.name}
                            onChange={handleChange}
                            className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 focus:border-accent focus:outline-none"
                            placeholder="Nora Modeler"
                        />
                    </label>

                    <label className="block text-sm text-white/80">
                        Email address
                        <input
                            type="email"
                            name="email"
                            value={formState.email}
                            onChange={handleChange}
                            required
                            className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 focus:border-accent focus:outline-none"
                            placeholder="you@modelerman.com"
                        />
                    </label>

                    {mode === "register" && (
                        <label className="block text-sm text-white/80">
                            Password
                            <input
                                type="password"
                                name="password"
                                value={formState.password}
                                onChange={handleChange}
                                required
                                className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 focus:border-accent focus:outline-none"
                                placeholder="Minimum 6 characters"
                            />
                        </label>
                    )}

                    {error && <p className="text-sm text-red-400">{error}</p>}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full rounded-xl bg-accent text-black font-semibold py-3 disabled:opacity-60"
                    >
                        {isLoading ? "Processing..." : mode === "login" ? "Sign in" : "Create free account"}
                    </button>
                </form>

                <p className="mt-4 text-xs text-white/60">
                    One account unlocks Store, Education, Services, and Figur Lab. We never share your email.
                </p>
            </div>
        </div>
    );
};

export default AuthDialog;
